import { useEffect, useRef } from "react";

import { track } from "@/lib/analytics/client";

/**
 * The multi-step flows worth measuring. A closed union rather than a free
 * string so a flow name cannot become a place user data leaks into, and so the
 * set of flows stays reviewable in one place.
 */
/**
 * Ordered numeric flows.
 *
 * "onboarding" was REMOVED, not forgotten. Its viewStep is an overloaded
 * integer (see lib/analytics/onboarding-steps.ts) — the same number means
 * different screens on different paths, so a numeric funnel over it produced
 * data that could not be compared between tenants. Onboarding now emits
 * onboarding.step.* with semantic names instead. Do not add it back here.
 */
export type FunnelFlow = "registration";

/**
 * Records progress through an ORDERED, numeric flow.
 *
 * Emits `funnel.step.entered` whenever the step changes, and
 * `funnel.step.completed` — carrying how long the step took — only when the
 * user moves FORWARD. Going back does not complete a step; that would report
 * a step as finished when the user was actually struggling with it, which
 * inverts the exact signal this is here to produce.
 *
 * ABANDONMENT IS NOT EMITTED, deliberately. There is no reliable
 * "user left" event on the web: unload handlers are best-effort and are
 * skipped outright when a tab is discarded or a phone kills the app, so an
 * `.abandoned` event would be missing precisely for the users who abandoned
 * hardest. Instead it is derived at query time — an `.entered` with no
 * matching `.completed` in the same session IS the abandonment, and that
 * derivation is exact because it needs nothing from a dying page.
 *
 * Pass `null` while the step is still unknown (loading), so a placeholder is
 * never recorded as a real step.
 */
/** What a step transition should emit. Pure, so it can be tested directly. */
export interface FunnelTransition {
  completed: { step: number; durationMs: number } | null;
  entered: number | null;
}

/**
 * Decides what a move from `prior` to `step` emits.
 *
 * Split out of the hook so the rules below are testable without a React
 * renderer — they are the part that carries the actual reasoning, and the hook
 * is just wiring.
 */
export function resolveFunnelTransition(
  prior: { step: number; enteredAt: number } | null,
  step: number | null,
  now: number,
): FunnelTransition {
  const none: FunnelTransition = { completed: null, entered: null };
  // Step not yet known (still loading) — recording a placeholder as a real
  // step would invent traffic on whichever step happens to be the default.
  if (step === null) return none;
  // Re-render with no actual movement.
  if (prior?.step === step) return none;
  return {
    // Only forward movement completes a step. Going BACK means the user was
    // struggling, and marking the step complete there would invert the signal.
    completed:
      prior !== null && step > prior.step
        ? { step: prior.step, durationMs: Math.round(now - prior.enteredAt) }
        : null,
    entered: step,
  };
}

export function useFunnelProgress(flow: FunnelFlow, step: number | null): void {
  const previous = useRef<{ step: number; enteredAt: number } | null>(null);

  useEffect(() => {
    const now = performance.now();
    const { completed, entered } = resolveFunnelTransition(
      previous.current,
      step,
      now,
    );
    if (completed !== null) {
      track("funnel.step.completed", { flow, ...completed });
    }
    if (entered !== null) {
      track("funnel.step.entered", { flow, step: entered });
      previous.current = { step: entered, enteredAt: now };
    }
  }, [flow, step]);
}
